/**
 * Generic auto-reminder data hooks - resource-agnostic, so the same status
 * button + details modal work for transfers, claims and inspections. Parameterised
 * by `resourceType` + `resourceUuid`; backed by the generic
 * `/api/reminders/<type>/<uuid>/` surface. Transfers sit behind the
 * `auto_reminders` flag; claims do not.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import api from '@/admin/lib/api'

export interface AutoReminderStatus {
  resource_type: string
  resource_uuid: string
  label: string
  enabled: boolean
  interval_days: number | null
  max_reminders: number | null
  sent_count: number
  last_sent_at: string | null
  next_send_at: string | null
  is_capped: boolean
  is_due: boolean
  template_name: string | null
  template_slug: string | null
  supports_cadence_override: boolean
  supports_auto_cancel: boolean
  auto_cancel_enabled: boolean
  configured: boolean
  recipient_email?: string
  /** Claims: a document request is open (sent, and the claim still waits on it). */
  has_active_request?: boolean
  /** Claims: the record made its own auto-cancel choice instead of following the setting. */
  auto_cancel_overridden?: boolean
  /** The viewer may turn reminders on or off here. */
  can_manage?: boolean
}

export interface SetReminderPayload {
  enabled?: boolean
  interval_days?: number | null
  max_reminders?: number | null
  /** `null` drops the record's own choice so it follows the setting again. */
  auto_cancel?: boolean | null
}

const statusKey = (resourceType: string, uuid: string | null) => [
  'auto-reminder',
  resourceType,
  uuid,
]

/**
 * Reminder status for one record, fetched once a uuid is present. A record type
 * behind a flag (transfers) is gated by its caller and the API. The parent
 * detail query is the caller's concern; this key is independent so the two
 * never collide. `refreshOn` refetches when it changes (e.g. the record's
 * workflow state).
 */
export function useReminderStatus(
  resourceType: string,
  uuid: string | null,
  options?: { enabled?: boolean; refreshOn?: string | null },
) {
  return useQuery<AutoReminderStatus>({
    queryKey: [...statusKey(resourceType, uuid), options?.refreshOn ?? null],
    queryFn: () =>
      api.get(`/reminders/${resourceType}/${uuid}/`).then((r) => r.data),
    enabled: !!uuid && (options?.enabled ?? true),
  })
}

/**
 * Enable / disable / reconfigure a record's reminder. Invalidates the reminder
 * status AND the parent detail query (`[resourceType, uuid]`, the house
 * detail-key convention - e.g. `['transfer', uuid]`) so a record-level
 * "auto reminder on" indicator refreshes too.
 */
export function useSetReminder(resourceType: string) {
  const qc = useQueryClient()
  return useMutation<
    AutoReminderStatus,
    Error,
    { uuid: string } & SetReminderPayload
  >({
    mutationFn: ({ uuid, ...body }) =>
      api.post(`/reminders/${resourceType}/${uuid}/`, body).then((r) => r.data),
    onSuccess: (_data, { uuid }) => {
      qc.invalidateQueries({ queryKey: statusKey(resourceType, uuid) })
      qc.invalidateQueries({ queryKey: [resourceType, uuid] })
    },
  })
}

/** Cancel (disable) a record's reminder - the explicit, audited endpoint. */
export function useCancelReminder(resourceType: string) {
  const qc = useQueryClient()
  return useMutation<AutoReminderStatus, Error, { uuid: string }>({
    mutationFn: ({ uuid }) =>
      api
        .post(`/reminders/${resourceType}/${uuid}/cancel/`)
        .then((r) => r.data),
    onSuccess: (_data, { uuid }) => {
      qc.invalidateQueries({ queryKey: statusKey(resourceType, uuid) })
      qc.invalidateQueries({ queryKey: [resourceType, uuid] })
    },
  })
}
